import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import Paper from '@mui/material/Paper';
import Box from "@mui/material/Box";
import { Controller, FieldErrors, set, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { postNewItemValidationSchema } from "../../../constants/validationSchemas";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Autocomplete, CircularProgress, debounce, FormControl, FormGroup, InputBase, Switch, Select, MenuItem, InputLabel } from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from "@mui/x-date-pickers";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { NotificationType } from "../../../constants/global";
import { routeConfigs } from "../../../navigation/routeConfig";
import { useNavigate } from "react-router-dom";
import { ToasterContext } from "../../../components/shared/Toaster/ToasterContext";
import { useMutationCreateProduct } from "../../../queries/product/hooks/useMutationCreateProduct";
import { useQueryPackages } from "../../../queries/product/hooks/useQueryPackages";
import { useQueryShippingMethods } from "../../../queries/product/hooks/useQueryShippingMethod";
import { UserContext } from "../../../hooks/userContext";
import { useQueryUniqueUsers } from "../../../queries/pharmacy/hooks/useQueryUniqueUsers";
import { useAuth } from "../../../hooks/useAuth";
import axios from 'axios';
import { red } from "@mui/material/colors";
import { filterNdcOptions, formatSearchNdcNumber, normalizeNdc } from "../../../components/utils/utils";
import dayjs from "dayjs";

export const NewSellItem: React.FunctionComponent = () => {
  const { id: userId }: any = useContext(UserContext);
  const { id }: any = useContext(UserContext);
  const { token } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useContext(ToasterContext);
  const [drugData, setDrugData] = useState<any>(null);
  const [refrigerated, setRefrigerated] = useState<boolean>(false);
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [fdaLoading, setFdaLoading] = useState<boolean>(false);
  const [type, setType] = useState<string>('FULL');
  const [sealed, setSealed] = useState<string>('');
  const [packageConditionType, setPackageConditionType] = useState<string>('');
  const [packageAvailable, setPackageAvailable] = useState<number>(1);
  const [price, setPrice] = useState<string>('');
  const [quantityPerPack, setQuantityPerPack] = useState<string>('');
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [options, setOptions] = useState<any>([]);
  const [notesValue, setNotesValue] = useState<string>('');
  const [notesFocused, setNotesFocused] = useState<boolean>(false);
  const [fdaError, setFdaError] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [useMedispan, setUseMedispan] = useState<boolean>(true);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [restrictedNdcs, setRestrictedNdcs] = useState<string[]>([]);
  const [isRestrictedUser, setIsRestrictedUser] = useState<boolean>(false);
  const [restrictionDataLoading, setRestrictionDataLoading] = useState<boolean>(true);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const packageConditionRef = useRef<HTMLDivElement>(null);
  const expDateRef = useRef<HTMLDivElement>(null);
  const isSubmittingRef = useRef<boolean>(false);
  const medispanAbortControllerRef = useRef<AbortController | null>(null);

  const { register, handleSubmit, formState: { errors }, control, setValue, trigger } = useForm({
    resolver: yupResolver(postNewItemValidationSchema)
  });

  const { data: pharmacyUsersData, loading: pharmacyUsersLoading } = useQueryUniqueUsers({
    variables: { where: { id: +userId } },
  });

  const { data: packagesData, loading: packagesLoading } = useQueryPackages();
  const { data: shippingMethodsData, loading: shippingMethodsLoading } = useQueryShippingMethods();
  const [createProduct, { loading }] = useMutationCreateProduct();

  // Fetch restriction data early
  useEffect(() => {
    const fetchRestrictionData = async () => {
      try {
        const userData = pharmacyUsersData?.findUniqueUser;
        const hasRestrictedRole = userData?.UserRoles?.some(
          (ur: any) => ur.Role?.key === 'restricted'
        );
        
        setIsRestrictedUser(hasRestrictedRole || false);
        
        if (hasRestrictedRole) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/users/restricted-ndcs?userId=${userId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          setRestrictedNdcs((response.data.ndcs || []).map((ndc: string) => normalizeNdc(ndc)));
        } else {
          setRestrictedNdcs([]);
        }
      } catch (error) {
        console.error('Error fetching restriction data:', error);
        setRestrictedNdcs([]);
      } finally {
        setRestrictionDataLoading(false);
      }
    };
    
    if (pharmacyUsersData?.findUniqueUser) {
      fetchRestrictionData();
    }
  }, [pharmacyUsersData, userId]);

  const normilzeNdc = (value: string) => {
    return value.replace(/-/g, '');
  }

  const checkIfNdc = (value: string): boolean => {
    // Treat values that start with digits as potential NDC search.
    return /^\d{3}/.test(value);
  }

  const search = useCallback(debounce((value: string) => {

    const data = JSON.stringify({
      "size": 50,
      "query": {
        "bool": {
          "should": [
            {
              "wildcard": {
                "product_ndc": `*${formatSearchNdcNumber(value)}*`
              }
            },
            {
              "match_phrase_prefix": {
                "generic_name": `${value}`,
              }
            },
            {
              "match_phrase_prefix": {
                "brand_name": `${value}`,
              }
            },
          ],
          "must_not": {
            "exists": {
              "field": "dea_schedule"
            }
          }
        }
      }
    });

    axios.post('https://ndc.tradenetrx.com/drug-fda/_search', data, {
      headers: {
        'Content-Type': 'application/json'
      },
      auth: {
        username: 'web-tradenetrx',
        password: 'zjm5bga-XKW8qgu4fvy',
      },
    })
      .then((response) => {
        setFdaLoading(false);
        if (response.data.hits.hits.length === 0) {
          setFdaError('Item with provided name and/or NDC was not found in FDA database. Please try a different name or continue manual entry below.');
        } else {
          setFdaError('');
          setOptions(response.data.hits.hits);
        }
      })
      .catch((error) => {
        setFdaLoading(false);
        addToast({ type: NotificationType.Error, title: error.message });
      });
  }, 1000), []);

  const capitalizeWords = (str: string) => {
    if (!str) return str;
    return str.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };


  const handleMedispanSearch = async (value: string) => {
    if (medispanAbortControllerRef.current) {
      medispanAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    medispanAbortControllerRef.current = abortController;

    try {
      setFdaLoading(true);
      const api = `${process.env.REACT_APP_API_URL}/medispan/get?searchTerm=${encodeURIComponent(value)}`;

      const response = await axios.get(api, { signal: abortController.signal });
      setFdaLoading(false);

      if (!Array.isArray(response.data)) {
        setFdaError('Item with provided name and/or NDC was not found in FDA database. Please try a different name or continue manual entry below.');
        return;
      }

      const filteredData = response.data
        .filter((item: any) => item.product_ndc !== null)
        .filter((item: any) => {
          const usingNdc = checkIfNdc(value);
          if (usingNdc) {
            const searchNdc = normilzeNdc(value);
            const productNdc = item.product_ndc ? normilzeNdc(item.product_ndc) : null;
            const packageNdc = item.package_ndc ? normilzeNdc(item.package_ndc) : null;

            return (productNdc && productNdc.startsWith(searchNdc)) ||
              (packageNdc && packageNdc.startsWith(searchNdc));
          } else {
            return true;
          }
        })
        .map((item: any) => {
          // Capitalize the drug name
          if (item.brand_name) {
            item.brand_name = capitalizeWords(item.brand_name);
          }
          if (item.generic_name) {
            item.generic_name = capitalizeWords(item.generic_name);
          }
          if (item.substance_name) {
            item.substance_name = capitalizeWords(item.substance_name);
          }
          return item;
        });



      if (filteredData.length === 0) {
        setFdaError('Item with provided name and/or NDC was not found in FDA database. Please try a different name or continue manual entry below.');
      } else {
        setFdaError('');
        setOptions(filteredData);
      }
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED') {
        return;
      }
      setFdaLoading(false);
      addToast({
        type: NotificationType.Error,
        title: error.response ? error.message : "Server side error"
      });
    }
  };

  const debouncedMedispanSearch = useCallback(debounce((value: string) => {
    handleMedispanSearch(value);
  }, 500), []);

  useEffect(() => {
    return () => {
      if (medispanAbortControllerRef.current) {
        medispanAbortControllerRef.current.abort();
      }
    };
  }, [debouncedMedispanSearch]);

  const handleSearch = (value: string) => {
    const trimmedValue = value.trimStart();
    setOptions([]);
    setFdaError('');

    if (trimmedValue.length >= 3) {
      if (useMedispan) {
        setFdaLoading(true);
        debouncedMedispanSearch(trimmedValue);
      } else {
        search(trimmedValue);
      }
    } else {
      setFdaLoading(false);
    }
  }

  function generateDrugDescription(data: any) {
    if (!data) return '';
    const {
      generic_product_pack_code,
      package_description_code,
      package_quantity,
      package_size,
      package_size_uom,
      unit_dose_unit_use_pkg_code,
    } = data;
    const isUnitDose = unit_dose_unit_use_pkg_code === 'X' ? 'Yes' : 'No';
    return `Pack of ${package_quantity} units, each containing ${package_size} ${package_size_uom}, Unit Dose: ${isUnitDose}, Packaging Code: ${package_description_code}, Pack Code: ${generic_product_pack_code}`;
  }


  const handleAutofill = (value: any) => {
    if (value) {
      console.log("value", value);
      setValue('ndc', value.package_ndc || value.product_ndc, { shouldValidate: true });
      setValue('drugName', value.brand_name || value.generic_name, { shouldValidate: true });
      setValue('labelerName', value.labeler_name, { shouldValidate: true });
      setValue('form', value.dosage_form, { shouldValidate: true });
      if (value.active_ingredients) {
        setValue('strength', value.active_ingredients[0].strength, { shouldValidate: true });
      }
      setValue('notes', value.package_description || generateDrugDescription(value.gppc_data), { shouldValidate: true });
      setNotesFocused(true)
      setDrugData(value);
      if (value.storage_condition === "R" || value.storage_condition === "F") {
        setRefrigerated(true);
      } else {
        setRefrigerated(false);
      }

      const formatedNdc = value.product_ndc?.replace(/-/g, "");
      if (!formatedNdc) return;
      const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formatedNdc}`;
      axios.get(imageUrlApi).then((response) => {
        setImageUrl(response.data.imageUrl);
      });

    } else {
      setValue('ndc', '');
      setValue('drugName', '');
      setValue('labelerName', '');
      setValue('form', '');
      setValue('strength', '');
      setValue('notes', '');
      setDrugData(null);
    }
  }

  const handleChangePackageAvailable = (packageAvailable: number, isPlus: boolean = true) => {
    if (!isPlus && packageAvailable === 0) {
      return
    }

    if (isPlus) {
      setPackageAvailable(packageAvailable + 1)
    } else {
      setPackageAvailable(packageAvailable - 1);
    }
  }

  const handleChangeRefrigerated = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRefrigerated(event.target.checked);
  };

  const handleNewSellItemSubmit = (values: any) => {
    // Prevent duplicate submissions
    if (isSubmittingRef.current || loading) {
      return;
    }

    // Don't proceed if restriction data is still loading
    if (restrictionDataLoading) {
      addToast({ 
        type: NotificationType.Error, 
        title: 'Please wait, loading restriction data...' 
      });
      return;
    }

    // Check if restricted user trying to create with restricted NDC
    if (isRestrictedUser && restrictedNdcs.includes(normalizeNdc(values.ndc))) {
      addToast({ 
        type: NotificationType.Error, 
        title: 'This NDC is restricted for you' 
      });
      return;
    }

    // Manual validation for partial quantity
    if (type === 'PARTIAL' && (!values.partialQuantity || values.partialQuantity.trim() === '')) {
      addToast({ type: NotificationType.Error, title: 'Partial quantity is required when packaging type is Partial' });
      return;
    }

    // Manual validation for package condition
    if (!values.packageConditionType || values.packageConditionType.trim() === '') {
      addToast({ type: NotificationType.Error, title: 'Package condition is required' });
      // Scroll to package condition field
      setTimeout(() => {
        packageConditionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    const newObject = { ...values };
    const packages: any = Object.keys(newObject)
      .filter((key) => key.includes("packageCondition-"))
      .reduce((obj, key) => {
        return Object.assign(obj, {
          [key]: newObject[key]
        });
      }, {});

    const data: any = [];
    Object.keys(packages).forEach((item: string) => {
      if (packages[item]) {
        const packageIds: string[] = item.split('-');
        data.push({ packageId: +packageIds[1] });
      }
    });

    const requestData = {
      drugName: values.drugName,
      ndc: values.ndc,
      lotNumber: values.lotNumber,
      expDate: values.expDate,
      type: type === 'FULL' ? 1 : 2,
      packageCondition: type === 'FULL' ? (sealed === 'SEALED' ? 1 : 2) : 2, // Default to 2 (Non-Sealed) for partial
      sealDetails: values.packageConditionType || '',
      labelerName: values.labelerName,
      form: values.form,
      maxPrice: '0',
      minPrice: '0',
      notes: values.notes,
      packaging: values.partialQuantity || '',
      price: values.price,
      priceOption: 1,
      quantity: packageAvailable,
      quantityPerPack: +values.quantityPerPack,
      storage: 'Normal',
      strength: values.strength,
      unitPrice: `${unitPrice}`,
      isFrozen: refrigerated,
      wacDiscount: 20,
      ProductPackages: {
        createMany: {
          data: data,
          skipDuplicates: false
        }
      },
      Pharmacy: {
        connect: {
          id: pharmacyUsersData.findUniqueUser.PharmacyUsers[0].pharmacyId
        }
      },
      ProductShipping: {
        create: {
          ShippingMethod: {
            connect: {
              id: shippingMethodsData.findManyShippingMethod[0].id
            }
          },
          isPayable: false,
        }
      },
      User: {
        connect: {
          id: +id
        }
      }
    }

    isSubmittingRef.current = true;
    createProduct({ variables: { data: requestData } }).then(() => {
      isSubmittingRef.current = false;
      navigate(routeConfigs.sell.path);
    }).catch((error: any) => {
      isSubmittingRef.current = false;
      addToast({ type: NotificationType.Error, title: error.message });
    });
  }

  useEffect(() => {
    if (price !== '' && quantityPerPack !== '') {
      const unitPrice = Number(price.replace(/[^0-9.-]+/g, "")) / + Number(quantityPerPack);

      setUnitPrice(!unitPrice ? 0 : unitPrice)
    }
  }, [price, quantityPerPack]);

  const scrollToExpDate = () => {
    setTimeout(() => {
      expDateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleInvalidSubmit = (formErrors: FieldErrors) => {
    if (formErrors.expDate) {
      scrollToExpDate();
    }
  };

  // Scroll to package condition field when validation error occurs
  useEffect(() => {
    if (errors.expDate) return;
    if (errors.packageConditionType || errors.sealed) {
      setTimeout(() => {
        packageConditionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [errors.packageConditionType, errors.sealed, errors.expDate]);

  if (restrictionDataLoading || packagesLoading || shippingMethodsLoading || pharmacyUsersLoading) {
    return <CircularProgress sx={{ position: 'absolute', left: 'calc(150px + 50%)', top: '50%' }} color="inherit" />;
  }

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box component="form" onSubmit={handleSubmit(handleNewSellItemSubmit, handleInvalidSubmit)} noValidate>
        <Typography component="h1" variant="h5" sx={{ m: 2 }}>Product Information</Typography>
        <Grid container sx={{ p: 2 }} spacing={3}>
          {/*  <Grid item xs={12}>
          
            <Switch value={useMedispan} onChange={(event) => {
              setUseMedispan(event.target.checked);
              setInputValue('');
              setOptions([]);
              setFdaError('');
            }} />
            <FormLabel id='medispan-label'>Use Medispan</FormLabel>
          </Grid> */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={useMedispan ? options : filterNdcOptions(options, inputValue)}
              filterOptions={(option: any) => option}
              inputValue={searchInputValue}
              getOptionLabel={(option: any) => {
                if (typeof option === 'string') return option;
                if (!option) return '';
                return `${option?.brand_name || option?.generic_name} - ${option.product_ndc}`;
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={drugData}
              freeSolo
              onChange={(event: any, newValue: string | null) => {
                if (newValue && typeof newValue === 'object') {
                  handleAutofill(newValue);
                }
              }}
              onInputChange={(event: any, newValue: string) => {
                const normalizedValue = newValue.trimStart();
                setSearchInputValue(normalizedValue);
                if (event?.type === 'change') {
                  setInputValue('');
                  handleSearch(normalizedValue);
                  const isNumber = /^\d+$/.test(normalizedValue);

                  if (isNumber && normalizedValue.length === 11) {
                    setInputValue(normalizedValue);
                  }
                }
              }}
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    label="Drug name or NDC number"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {fdaLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                  {fdaError !== '' && (
                    <Box sx={{ fontSize: '12px', mt: 1, color: red[500] }}>
                      {fdaError}
                    </Box>
                  )}
                </>
              )}
            />
          </Grid>
        </Grid>
        <Grid container sx={{ p: 2 }} spacing={3}>
          <>
            <Grid item xs={12}>
              <Divider sx={{ borderBottomWidth: '2px', borderBottomColor: '#000000' }} />
            </Grid>
          </>
          <Grid item xs={12} md={6}>
            <TextField
              disabled
              label="NDC Number"
              id="ndc"
              fullWidth
              required
              InputLabelProps={{ shrink: !!drugData?.product_ndc }}
              defaultValue={drugData?.product_ndc || ''}
              helperText={errors.ndc?.message?.toString()}
              {...register('ndc')}
              error={!!errors.ndc}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Lot Number"
              id="lotNumber"
              fullWidth
              required
              helperText={errors.lotNumber?.message?.toString()}
              {...register('lotNumber')}
              error={!!errors.lotNumber}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              disabled
              label="Drug name"
              id="drugName"
              fullWidth
              required
              InputLabelProps={{ shrink: !!drugData?.generic_name }}
              defaultValue={drugData?.brand_name || drugData?.generic_name}
              helperText={errors.drugName?.message?.toString()}
              {...register('drugName')}
              error={!!errors.drugName}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              disabled
              label="Manufacturer"
              id="labelerName"
              fullWidth
              required
              InputLabelProps={{ shrink: !!drugData?.labeler_name }}
              defaultValue={drugData?.labeler_name}
              helperText={errors.labelerName?.message?.toString()}
              {...register('labelerName')}
              error={!!errors.labelerName}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              disabled
              label="Dosage form"
              id="form"
              fullWidth
              required
              InputLabelProps={{ shrink: !!drugData?.dosage_form }}
              defaultValue={drugData?.dosage_form}
              helperText={errors.form?.message?.toString()}
              {...register('form')}
              error={!!errors.form}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Strength"
              id="strength"
              fullWidth
              required
              InputLabelProps={{ shrink: !!drugData?.active_ingredients }}
              defaultValue={drugData?.active_ingredients ? drugData?.active_ingredients[0]?.strength : ''}
              helperText={errors.strength?.message?.toString()}
              {...register('strength')}
              error={!!errors.strength}
            />
          </Grid>
          <Grid item xs={12} md={6} ref={expDateRef}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                control={control}
                name="expDate"
                render={({
                  field: { onChange, value },
                }) => (
                  <FormControl fullWidth>
                    <DatePicker
                      value={value || ""}
                      onChange={(date) => {
                        onChange(date?.isValid ? date : "");
                        void trigger("expDate");
                      }}
                      open={openDatePicker}
                      onClose={() => setOpenDatePicker(false)}
                      views={['month', 'year']}
                      minDate={dayjs().add(1, 'month').startOf('month')}
                      format={"MM/YYYY"}
                      label="Expiration date"
                      slotProps={{
                        textField: {
                          error: !!errors.expDate,
                          helperText: errors.expDate?.message?.toString(),
                          onClick: () => setOpenDatePicker(true)
                        },
                      }}
                    />
                  </FormControl>
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  disabled={useMedispan}
                  checked={refrigerated}
                  onChange={(event) => {
                    handleChangeRefrigerated(event);
                    // setShippingMethod(3);
                  }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              }
              label="Is product refrigerated"
            />

          </Grid>
          <Grid item xs={12} md={6}>
            <React.Fragment>
              <Grid item xs={12}>
                <FormLabel id='type-label'>Packaging</FormLabel>
              </Grid>
              <Controller
                name='type'
                control={control}
                defaultValue={type}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    aria-labelledby='type-label'
                    row id='type'
                    value={type}
                    onChange={(event) => {
                      setType(event.target.value);
                      if (event.target.value === 'PARTIAL') {
                        setSealed('NON_SEALED');
                        setPackageConditionType('');
                      } else {
                        setSealed('');
                        setPackageConditionType('');
                      }
                      field.onChange(event);
                    }}
                  >
                    <FormControlLabel
                      value="FULL"
                      control={<Radio />}
                      label="Full"
                    />
                    <FormControlLabel
                      value="PARTIAL"
                      control={<Radio />}
                      label="Partial"
                    />
                    {type === 'PARTIAL' && (
                      <TextField
                        sx={{ mt: 1 }}
                        label="Partial quantity"
                        id="partialQuantity"
                        {...register('partialQuantity')}
                        fullWidth
                        required
                        helperText={errors.partialQuantity?.message?.toString()}
                        error={!!errors.partialQuantity}
                      />
                    )}
                  </RadioGroup>
                )}
              />
            </React.Fragment>
          </Grid>
          <Grid item xs={12} md={6} ref={packageConditionRef}>
            <React.Fragment>
              <Grid item xs={12}>
                <FormLabel id='sealed-label' error={!!errors.sealed}>Sealed</FormLabel>
              </Grid>
              <Controller
                name='sealed'
                control={control}
                render={({ field }) => (
                  <>
                    <RadioGroup
                      {...field}
                      aria-labelledby='sealed-label'
                      row
                      id='sealed'
                      value={sealed}
                      onChange={(event) => {
                        setSealed(event.target.value);
                        setPackageConditionType('');
                        field.onChange(event);
                      }}
                    >
                      <FormControlLabel
                        value="SEALED"
                        control={<Radio />}
                        label="Sealed"
                        disabled={type === 'PARTIAL'}
                      />
                      <FormControlLabel
                        value="NON_SEALED"
                        control={<Radio />}
                        label="Non-Sealed"
                        disabled={type === 'PARTIAL'}
                      />
                    </RadioGroup>
                    {errors.sealed && typeof errors.sealed.message === 'string' && (
                      <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>{errors.sealed.message}</Box>
                    )}
                  </>
                )}
              />
              <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                <FormLabel id='package-condition-type-label'>Package Condition</FormLabel>
              </Grid>
              <Controller
                name='packageConditionType'
                control={control}
                defaultValue={packageConditionType}
                render={({ field }) => (
                  <FormControl fullWidth required>
                    <InputLabel id="package-condition-type-label">Package Condition</InputLabel>
                    <Select
                      {...field}
                      labelId="package-condition-type-label"
                      id="packageConditionType"
                      label="Package Condition"
                      value={field.value || ''}
                      onChange={(event) => {
                        const value = event.target.value;
                        setPackageConditionType(value);
                        field.onChange(value);
                      }}
                      disabled={(type === 'FULL' && !sealed) || (type === 'PARTIAL' && sealed !== 'NON_SEALED')}
                      error={!!errors.packageConditionType}
                    >
                      {sealed === 'SEALED' && [
                        <MenuItem value="SEALED_ORIGINAL_CONTAINER" key="SEALED_ORIGINAL_CONTAINER">Sealed Original Container</MenuItem>,
                        <MenuItem value="SEALED_ORIGINAL_BOTTLE_TORN_LABEL" key="SEALED_ORIGINAL_BOTTLE_TORN_LABEL">Sealed Original Bottle/Torn or label residue</MenuItem>
                      ]}
                      {sealed === 'NON_SEALED' && [
                        <MenuItem value="OPEN_ORIGINAL_CONTAINER" key="OPEN_ORIGINAL_CONTAINER">Open Original Container</MenuItem>,
                        <MenuItem value="OPEN_PRESCRIPTION_BOTTLE" key="OPEN_PRESCRIPTION_BOTTLE">Open in a Prescription bottle</MenuItem>,
                        <MenuItem value="OPEN_ORIGINAL_BOTTLE_TORN_LABEL" key="OPEN_ORIGINAL_BOTTLE_TORN_LABEL">Open Original bottle/Torn on label residue</MenuItem>
                      ]}
                      {!sealed && (
                        <MenuItem value="" disabled key="empty">Please select Sealed or Non-Sealed first</MenuItem>
                      )}
                    </Select>
                    {errors.packageConditionType && (
                      <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
                        {typeof errors.packageConditionType.message === 'string' ? errors.packageConditionType.message : ''}
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            </React.Fragment>
          </Grid>
          <Grid item xs={12} md={12}>
            <Grid item xs={12} sx={{ mb: 2 }}>
              <FormLabel>Packages Available</FormLabel>
            </Grid>
            <Paper sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: 140 }}>
              <IconButton
                size='small'
                color="primary"
                sx={{ p: '4px' }}
                onClick={() => handleChangePackageAvailable(packageAvailable, false)}
              >
                <RemoveIcon />
              </IconButton>
              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
              <InputBase
                sx={{ flex: 1, input: { textAlign: "center" } }}
                value={packageAvailable}
              />
              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
              <IconButton
                size='small'
                color="primary"
                sx={{ p: '4px' }}
                onClick={() => handleChangePackageAvailable(packageAvailable)}
              >
                <AddIcon />
              </IconButton>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid item xs={12}>
              <FormLabel>Package Condition (Optional)</FormLabel>
            </Grid>
            {packagesData.findManyPackage.map((option: any) => (
              <Controller
                key={option.id}
                name={`packageCondition-${option.id}`}
                control={control}
                render={({ field }) => (
                  <FormGroup {...field} sx={{ width: 250 }}>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={option.key === 'other' ? `${option.name} (Describe in Notes)` : option.name}
                    />
                  </FormGroup>
                )}
              />
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Notes"
              id="notes"
              fullWidth
              rows={6}
              multiline
              InputLabelProps={{
                shrink: Boolean(drugData?.package_description || notesValue || notesFocused)
              }}
              defaultValue={drugData?.package_description}
              helperText={errors.notes?.message?.toString()}
              {...register('notes')}
              onChange={(e) => {
                setValue('notes', e.target.value, { shouldValidate: true });
              }}
              onFocus={() => setNotesFocused(true)}
              onBlur={(e) => {
                // Only unfocus if there's no content in the field
                if (!e.target.value) {
                  setNotesFocused(false);
                }
              }}
              error={!!errors.notes}
            />

            {imageUrl && (
              <div style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "start",
                alignItems: "start",
              }} >
                <img
                  className=''
                  src={imageUrl}
                  alt="Product"
                  style={{
                    width: "auto",
                    height: "auto",
                  }}
                ></img>
              </div>
            )}

          </Grid>
        </Grid>
        <Divider sx={{ mt: 2 }} />
        <Typography component="h1" variant="h5" sx={{ m: 2 }}>Set Price</Typography>
        <Grid container sx={{ p: 2 }} spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Price"
              placeholder='$0.00'
              id="price"
              required
              fullWidth
              helperText={errors.price?.message?.toString()}
              {...register('price')}
              error={!!errors.price}
              onChange={(event) => {
                setValue('price', event.target.value, { shouldValidate: true });
                setPrice(event.target.value);
              }}
              onBlur={(event) => {
                setValue('price', event.target.value !== '' ? Number(event.target.value).toFixed(2) : '');
                setPrice(event.target.value !== '' ? Number(event.target.value).toFixed(2) : '');
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Original pack size"
              id="quantityPerPack"
              required
              fullWidth
              helperText={errors.quantityPerPack?.message?.toString()}
              {...register('quantityPerPack')}
              error={!!errors.quantityPerPack}
              onChange={(event) => {
                setValue('quantityPerPack', event.target.value, { shouldValidate: true });
                setQuantityPerPack(event.target.value)
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Unite price"
              placeholder='0.00 $'
              id="unitPrice"
              value={`$${unitPrice.toFixed(2)}`}
              required
              fullWidth
              disabled
            />
          </Grid>
        </Grid>
        <Divider sx={{ mt: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'end', flexDirection: 'row', m: 2 }}>
          <Button 
            type="submit" 
            size='large' 
            sx={{ width: 200 }} 
            variant='contained'
            disabled={loading}
          >
            Post Item
            {loading && <CircularProgress sx={{ ml: 2 }} size={14} color='inherit' />}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default NewSellItem;